import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Copy } from "lucide-react";

export default function Setup2FA() {
  const { user, refresh } = useAuth();
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(null);

  const start = async () => {
    const r = await api.post("/auth/2fa/setup");
    setQr(r.data.qr_data_uri);
    setSecret(r.data.secret);
  };

  const enable = async () => {
    try {
      const r = await api.post("/auth/2fa/enable", { totp_code: code });
      setRecovery(r.data.recovery_codes);
      toast.success("2FA activado");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <div className="flex items-center gap-3 mb-6">
        <ShieldCheck size={28} className="text-[#EF4444]" />
        <h1 className="font-display text-3xl">2FA — Autenticación en dos pasos</h1>
      </div>

      <div className="bg-[#111] border border-[#262626] rounded-md p-6">
        {user?.totp_enabled ? (
          <div className="text-emerald-400">2FA activado en tu cuenta. Al iniciar sesión te pediremos el código de Google Authenticator / Authy.</div>
        ) : !qr ? (
          <div className="space-y-4">
            <p className="text-neutral-400 text-sm">Activa 2FA con Google Authenticator, Authy o cualquier app TOTP compatible. Al finalizar recibirás códigos de respaldo.</p>
            <Button data-testid="2fa-start" onClick={start} className="bg-[#EF4444] hover:bg-[#DC2626]">Iniciar setup</Button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex flex-col items-center gap-3">
              <img src={qr} alt="Código QR TOTP" className="w-56 h-56 bg-white p-3 rounded-md" data-testid="2fa-qr" />
              <div className="text-xs text-neutral-500">O ingresa manualmente el código secreto:</div>
              <div className="flex items-center gap-2 bg-[#0A0A0A] px-3 py-2 rounded-md border border-[#262626]">
                <code className="text-sm text-white" data-testid="2fa-secret">{secret}</code>
                <button onClick={() => { navigator.clipboard.writeText(secret); toast.success("Copiado"); }} className="text-neutral-500 hover:text-white">
                  <Copy size={14} />
                </button>
              </div>
            </div>
            <div>
              <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Código de verificación</Label>
              <Input data-testid="2fa-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="6 dígitos" className="bg-[#171717] border-[#262626]" />
            </div>
            <Button data-testid="2fa-enable" onClick={enable} className="w-full bg-[#EF4444] hover:bg-[#DC2626]">Activar 2FA</Button>
          </div>
        )}
      </div>

      {recovery && (
        <div className="mt-6 bg-amber-500/10 border border-amber-500/30 rounded-md p-6">
          <div className="font-semibold text-amber-300 mb-2">Códigos de recuperación</div>
          <p className="text-neutral-400 text-sm mb-4">Guarda estos códigos en un lugar seguro. Cada uno se usa UNA vez si pierdes tu teléfono.</p>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm text-white">
            {recovery.map((c) => <div key={c} className="p-2 bg-[#0A0A0A] rounded border border-[#262626]" data-testid={`recovery-${c}`}>{c}</div>)}
          </div>
        </div>
      )}
    </div>
  );
}
